import { useEffect, useState } from "react";
import Modal from "../../../../components/common/Modal";
import { Controller, useForm, useFormContext, useWatch } from "react-hook-form";
import PrimaryButton from "../../../../components/common/PrimaryButton";
import TextInput, { InputType } from "../../../../components/forms/textInput";

import styles from "./styles.module.scss";
import { useTranslation } from "react-i18next";
import * as z from "zod";
import { fetchAndActivate, getValue } from "firebase/remote-config";
import { remoteConfig } from "../../../../firebase/firebase";
import { RemoteConfig } from "../../../../ampli/constants";
import {
  isValidSaudiPhoneNumber,
  normalizeSaudiNumber,
} from "../../../../setup/util/phoneNumber.helpers";
import { zodResolver } from "@hookform/resolvers/zod";
import { ESenderRole } from "../../../../setup/api/services/contract/models";

interface IAddPartyModalProps {
  handleClose: () => void;
  handleSave: (partyData: Inputs) => void;
  modalOpen: boolean;
  partyIndex: number;
  type: "customer" | "provider";
  mode: string;
}

export type Inputs = {
  id: string;
  rowId?: string;
  name: string;
  phoneNumber: string;
  amount: number | null;
  fee?: number;
  type: string;
  role?: string;
  isCompany?: boolean;
  companyUnifiedNumber?: string;
  companyName?: string;
  iban?: string;
  nationalAddress?: string;
  // Preserved through an edit so a prefilled, non-deletable party keeps its flag.
  nonDeletable?: boolean;
};

const AddPartyModal = ({
  handleClose,
  modalOpen,
  handleSave,
  type,
  partyIndex,
  mode,
}: IAddPartyModalProps) => {
  const [maxPrice, setMaxPrice] = useState(1000000);
  const { t } = useTranslation();

  const isDuplicate = (phoneNumber: string) => {
    const currentMilestoneIndex = watch("currentMilestoneIndex");
    const allParties = watch(`milestones.${currentMilestoneIndex}.parties`);

    return allParties?.some(
      (p: any, i: number) => p.phoneNumber === phoneNumber && partyIndex !== i
    );
  };

  const schema = z
    .object({
      id: z.string(),
      name: z.string(),
      phoneNumber: z.string(),
      amount: z
        .number()
        .nullable()
        .refine((val) => val !== null, {
          message: t("err_required"),
        }),
      fee: z.number().optional(),
      type: z.string().nonempty({ message: t("err_required") }),
      role: z.string().optional(),
      isCompany: z.boolean().optional(),
      companyUnifiedNumber: z.string().optional(),
      companyName: z.string().optional(),
      iban: z.string().optional(),
      nationalAddress: z.string().optional(),
    })
    .refine((data) => data.type !== "provider" || !!data.role, {
      path: ["role"],
      message: t("err_role_required_for_providers"),
    })
    .superRefine((data, ctx) => {
      const isBroker = data.role === ESenderRole.BROKER;
      const isCompanyParty = !!data.isCompany;

      if (!isBroker) {
        // name is always required
        if (!data.name || data.name.trim().length < 5) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["name"],
            message: t("err_min_chars", { min: 5 }),
          });
        } else if (data.name.trim().length > 100) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["name"],
            message: t("err_max_chars", { max: 100 }),
          });
        }

        // Individual/Company: require phoneNumber ALWAYS
        if (!isValidSaudiPhoneNumber(data.phoneNumber)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["phoneNumber"],
            message: t("err_invalid_phone"),
          });
        } else if (isDuplicate(data.phoneNumber)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["phoneNumber"],
            message: t("err_duplicate_phone"),
          });
        }

        if (isCompanyParty) {
          // Company: require companyName + companyUnifiedNumber
          if (!data.companyName || data.companyName.trim().length < 5) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ["companyName"],
              message: t("err_min_chars", { min: 5 }),
            });
          } else if (data.companyName.trim().length > 100) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ["companyName"],
              message: t("err_max_chars", { max: 100 }),
            });
          }

          if (!/^7\d{9}$/.test(data.companyUnifiedNumber || "")) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ["companyUnifiedNumber"],
              message: t("err_invalid_unified_number"),
            });
          }
        }

        if (
          data?.nationalAddress &&
          !/^[A-Za-z]{4}[0-9]{4}$/.test(data.nationalAddress)
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["nationalAddress"],
            message: t("err_invalid_national_address"),
          });
        }

        if (data?.iban && !/^SA\d{22}$/i.test(data.iban.replace(/\s/g, ""))) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["iban"],
            message: t("err_invalid_iban"),
          });
        }

        if (
          data?.amount !== null &&
          (data?.type === "customer" ? data?.amount < 1 : data?.amount < 0)
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["amount"],
            message: t("err_number_min", {
              min: data?.type === "customer" ? 1 : 0,
            }),
          });
        }
      } else if (data?.amount !== null && data?.amount < 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["amount"],
          message: t("err_number_min", { min: 0 }),
        });
      }
    });

  const { watch } = useFormContext();
  const {
    control: partyControl,
    reset,
    trigger,
    watch: partyWatch,
    setValue: setPartyValue,
    formState: { isValid, errors, isDirty },
  } = useForm<Inputs>({
    resolver: zodResolver(schema),
    mode: "onChange",
    defaultValues: {
      id: "",
      name: "",
      phoneNumber: "",
      amount: undefined,
      fee: 0,
      role: "",
      type: "customer",
      isCompany: false,
      companyUnifiedNumber: "",
      companyName: "",
      iban: "",
      nationalAddress: "",
    },
  });

  const roleFieldValue = useWatch({ control: partyControl, name: "role" });
  const isCompanyValue = useWatch({
    control: partyControl,
    name: "isCompany",
    defaultValue: false,
  });

  const currentMilestoneIndex = watch("currentMilestoneIndex") || 0;
  const parties = watch(`milestones.${currentMilestoneIndex}.parties`);

  useEffect(() => {
    if (parties?.[partyIndex]?.id && mode === "edit") {
      const party = parties[partyIndex];
      reset({
        ...party,
        isCompany:
          party.role === ESenderRole.BROKER
            ? false
            : !!party.companyUnifiedNumber,
      });
      setTimeout(() => trigger(), 0);
    } else {
      reset({
        id: `${type}-${partyIndex}-${Date.now()}-${Math.random()
          .toString(36)
          .substring(2, 9)}`,
        rowId: `${type}-${partyIndex}-${Date.now()}-${Math.random()
          .toString(36)
          .substring(2, 9)}`,
        name: "",
        phoneNumber: "",
        amount: null,
        fee: 0,
        type,
        role: "",
        isCompany: false,
        companyUnifiedNumber: "",
        companyName: "",
        iban: "",
        nationalAddress: "",
      });
    }
  }, [partyIndex, reset, parties, mode, type, trigger, modalOpen]);

  useEffect(() => {
    const fetchFirebaseConfig = async () => {
      await fetchAndActivate(remoteConfig);
      const maxPriceValue = getValue(
        remoteConfig,
        "max_product_price"
      ) as unknown as RemoteConfig;
      setMaxPrice(+maxPriceValue?._value);
    };
    fetchFirebaseConfig();
  }, []);

  useEffect(() => {
    setPartyValue("fee", 0);
  }, [setPartyValue]);

  function normalizePastedNumber(pasted: string): string {
    const digits = pasted.replace(/[^\d]/g, "");
    if (digits.length <= 1) return digits;
    const reversed = digits.split("").reverse().join("");
    const hasArabic = /[؀-ۿ٠-٩]/.test(pasted);
    return hasArabic ? reversed : digits;
  }

  const isBroker = roleFieldValue === ESenderRole.BROKER;

  return (
    <Modal
      customModalContainerClassName={styles.modalContainer}
      customModalClassName={styles.modal}
      visible={modalOpen}
      onCancel={() => {
        handleClose();
      }}>
      <div className={styles.container}>
        <h1 className={styles.title}>
          {type === "customer" ? t("lbl_add_customer") : t("lbl_add_provider")}
        </h1>

        {/* isCompany toggle — brokers are always individuals, so it is locked */}
        <Controller
          name="isCompany"
          control={partyControl}
          render={({ field }) => {
            const companySelected = !isBroker && !!field.value;
            return (
              <div className={styles.segmentedTabs}>
                <button
                  type="button"
                  disabled={isBroker}
                  className={`${styles.segmentedTab} ${
                    !companySelected ? styles.segmentedTabActive : ""
                  }`}
                  onClick={() => field.onChange(false)}>
                  {t("lbl_individual")}
                </button>
                <button
                  type="button"
                  disabled={isBroker}
                  className={`${styles.segmentedTab} ${
                    companySelected ? styles.segmentedTabActive : ""
                  } ${isBroker ? styles.segmentedTabDisabled : ""}`}
                  onClick={() => field.onChange(true)}>
                  {t("lbl_company")}
                </button>
              </div>
            );
          }}
        />

        {/* name is always shown */}
        <Controller
          name="name"
          control={partyControl}
          rules={{ required: true, max: 100, min: 5 }}
          render={({ field }) => (
            <TextInput
              {...field}
              maxCharacters={100}
              required
              disabled={isBroker}
              placeholder={t("lbl_name")}
              errorMessage={errors?.name?.message?.toString()}
            />
          )}
        />

        {/* company fields — only when isCompany */}
        {isCompanyValue && !isBroker && (
          <div className={styles.fieldRow2}>
            <Controller
              name="companyName"
              control={partyControl}
              render={({ field }) => (
                <TextInput
                  {...field}
                  maxCharacters={100}
                  required
                  placeholder={t("lbl_company_name")}
                  errorMessage={errors?.companyName?.message?.toString()}
                />
              )}
            />
            <Controller
              name="companyUnifiedNumber"
              control={partyControl}
              render={({ field }) => (
                <TextInput
                  {...field}
                  maxCharacters={10}
                  required
                  hint={t("lbl_unified_number_hint")}
                  placeholder={t("lbl_unified_number")}
                  errorMessage={errors?.companyUnifiedNumber?.message?.toString()}
                />
              )}
            />
          </div>
        )}

        <div className={styles.fieldRow2}>
          {/* phone — always shown */}
          <Controller
            name="phoneNumber"
            control={partyControl}
            rules={{ required: true }}
            render={({ field }) => (
              <TextInput
                {...field}
                maxCharacters={100}
                required
                onChange={(e) => {
                  const value = e?.target?.value.replace(/\s+/g, "");
                  if (isBroker) {
                    field?.onChange(value);
                  } else {
                    field?.onChange(normalizeSaudiNumber(value));
                  }
                }}
                onPaste={(e) => {
                  e.preventDefault();
                  const pasted = e.clipboardData.getData("text");
                  if (isBroker) {
                    field.onChange(pasted);
                  } else {
                    field.onChange(
                      normalizeSaudiNumber(normalizePastedNumber(pasted))
                    );
                  }
                }}
                type={isBroker ? InputType.DEFAULT : InputType.PHONE_NUMBER}
                disabled={isBroker}
                hint={isBroker ? undefined : t("lbl_mobile_number_hint")}
                placeholder={
                  isBroker ? t("lbl_identifier") : t("lbl_mobile_number")
                }
                errorMessage={errors?.phoneNumber?.message?.toString()}
              />
            )}
          />

          <Controller
            name="nationalAddress"
            control={partyControl}
            render={({ field }) => (
              <TextInput
                value={field.value ?? ""}
                name={field.name}
                onBlur={field.onBlur}
                handleChange={field.onChange}
                maxCharacters={8}
                disabled={isBroker}
                hint={isBroker ? undefined : t("lbl_national_address_hint")}
                placeholder={t("lbl_national_address")}
                errorMessage={errors?.nationalAddress?.message?.toString()}
              />
            )}
          />
        </div>

        <Controller
          name="iban"
          control={partyControl}
          render={({ field }) => (
            <TextInput
              {...field}
              value={field.value ?? ""}
              maxCharacters={29}
              disabled={isBroker}
              hint={isBroker ? undefined : t("lbl_iban_hint")}
              placeholder={t("lbl_iban")}
              errorMessage={errors?.iban?.message?.toString()}
            />
          )}
        />

        {type === "provider" && (
          <Controller
            name="role"
            control={partyControl}
            rules={{ required: true }}
            render={({ field }) => (
              <TextInput
                {...field}
                maxCharacters={100}
                required
                value={
                  field?.value === ESenderRole.BROKER
                    ? t("lbl_arbitrator_title")
                    : field?.value
                }
                disabled={isBroker}
                placeholder={t("lbl_role")}
                errorMessage={errors?.role?.message?.toString()}
              />
            )}
          />
        )}

        <Controller
          name="amount"
          control={partyControl}
          rules={{
            required: true,
            max: maxPrice,
            min: type === "customer" ? 1 : 0,
          }}
          render={({ field }) => (
            <TextInput
              {...field}
              value={field?.value !== null ? field?.value : ""}
              onChange={(event) => {
                field?.onChange(
                  event?.target?.value === "" ? null : +event?.target?.value
                );
              }}
              maxCharacters={100}
              required
              description={t("lbl_saudi_currency")}
              hint={type === "customer" ? t("lbl_amount_hint") : undefined}
              type={InputType.NUMBER}
              placeholder={t("lbl_value")}
              errorMessage={errors?.amount?.message?.toString()}
            />
          )}
        />

        <PrimaryButton
          disabled={mode === "edit" ? !isDirty || !isValid : !isValid}
          style={{
            marginTop: "8px",
            background: (mode === "edit" ? isValid && isDirty : isValid)
              ? "#0D44AD"
              : "#EEF0F5",
          }}
          onClick={() => {
            const data = partyWatch();
            if (!data.isCompany) {
              data.companyUnifiedNumber = "";
              data.companyName = "";
            }
            handleSave?.(data);
          }}>
          {mode === "add" ? t("lbl_add") : t("lbl_edit")}
        </PrimaryButton>
      </div>
    </Modal>
  );
};

export default AddPartyModal;
